import { useMemo } from 'react';
import { useSession } from '@/features/auth/hooks/useSession';
import { DiagOverlay } from '@/common/components/DiagPanel';
import { summarizeContainer, type DiagSnapshot } from '@/lib/diag';

/**
 * Composition-layer host for the #5105 diagnostics overlay.
 *
 * `DiagOverlay`/`DiagPanel` live in `common/` and must NOT import `features/auth`
 * (import-direction / ADR 024). This host — in the `app/` composition layer, which
 * may import `features/` — reads `useSession()` and injects a plain, redaction-safe
 * session snapshot as a prop. Mount once at the app root, INSIDE `SessionProvider`.
 */
export function DiagHost() {
  const {
    token,
    profile,
    status,
    containers,
    selectedContainer,
    containerReady,
    containersLoaded,
  } = useSession();

  const snapshot: DiagSnapshot = useMemo(
    () => ({
      authStatus: status,
      hasToken: Boolean(token),
      hasProfile: Boolean(profile),
      containersLoaded,
      containerReady,
      containersCount: containers?.length ?? 0,
      containers: (containers ?? []).map(summarizeContainer),
      selected: selectedContainer ? summarizeContainer(selectedContainer) : null,
    }),
    [token, profile, status, containers, selectedContainer, containerReady, containersLoaded],
  );

  return <DiagOverlay snapshot={snapshot} />;
}
